import React, { useState } from "react"
import {
  Button,
  Space,
  Typography,
  message,
  LockOutlined,
  MobileOutlined,
} from "@mimber/ui-web"
import { confirmSignIn, mapCognitoError } from "@mimber/auth"
import type { MFAMethodSelectorProps } from "./MFAMethodSelector.types"
import type { MFAMethod } from "@mimber/auth"

const { Paragraph, Title } = Typography

const METHOD_CONFIG: Record<
  string,
  {
    label: string
    description: string
    icon: React.ReactNode
    method: MFAMethod
  }
> = {
  TOTP: {
    label: "Authenticator App",
    description: "Use a code from your authenticator app",
    icon: <LockOutlined />,
    method: "totp",
  },
  SMS: {
    label: "Text Message (SMS)",
    description: "Get a code sent to your phone",
    icon: <MobileOutlined />,
    method: "sms",
  },
}

export const MFAMethodSelector: React.FunctionComponent<
  MFAMethodSelectorProps
> = ({ allowedMethods, onMethodSelected, onCancel, onUseRecoveryCode }) => {
  const [isLoading, setIsLoading] = useState<string | null>(null)

  const handleSelect = async (amplifyMethod: string) => {
    const config = METHOD_CONFIG[amplifyMethod]
    if (!config) return

    setIsLoading(amplifyMethod)
    try {
      await confirmSignIn(amplifyMethod)
      onMethodSelected(config.method)
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
    } finally {
      setIsLoading(null)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Title level={4}>Choose verification method</Title>
        <Paragraph>Select how you'd like to verify your identity</Paragraph>
      </div>

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {allowedMethods.map((amplifyMethod) => {
          const config = METHOD_CONFIG[amplifyMethod]
          if (!config) return null

          return (
            <Button
              key={amplifyMethod}
              block
              size="large"
              icon={config.icon}
              loading={isLoading === amplifyMethod}
              disabled={isLoading !== null}
              onClick={() => handleSelect(amplifyMethod)}
              data-testid={`mfa-method-${config.method}`}
              className="text-left h-auto py-3"
            >
              <span>
                <strong>{config.label}</strong>
                <br />
                <span className="text-gray-500 text-sm">
                  {config.description}
                </span>
              </span>
            </Button>
          )
        })}
      </Space>

      <div className="flex justify-between items-center mt-4">
        <Button
          type="link"
          onClick={onCancel}
          data-testid="mfa-method-cancel-button"
        >
          Cancel
        </Button>
        {onUseRecoveryCode && (
          <Button
            type="link"
            onClick={onUseRecoveryCode}
            data-testid="use-recovery-code-link"
          >
            Use recovery code
          </Button>
        )}
      </div>
    </>
  )
}
